// Per-page fingerprints of the 3D preview — which pages a quick preview must redraw.
//
// ─── WHY THIS EXISTS (owner, 2026-10-18 — « show the new version instantly ») ──
//
// After « Régénérer » the 3D preview waited for the full print rebuild: 3 to 4
// minutes of full-resolution originals for 36 pages, then a minute of
// rasterizing — to change one caption. The quick preview (lib/quickPreview.ts)
// redraws ONLY the pages that changed, with the SAME PDF code and the
// preview-size photos, in the admin's own browser, in seconds.
//
// To know which pages changed, every manifest now carries the fingerprints of
// what it was drawn from. These are computed from the very `AlbumPdfInput` the
// generator draws, so a page whose fingerprint is unchanged would be drawn
// byte-for-byte the same way (only photo sharpness differs between the quick
// and print passes, and photos are fingerprinted by FILE — `storage_path` —
// never by the signed URL that names the size).
//
// ⚠️ EVERY DOUBT REDRAWS. Anything that can move or restyle more than one page
// lives in the ALBUM key; a change there redraws the whole album. A missing or
// older-version fingerprint set also redraws everything. The cost of a wrong
// « unchanged » is a stale page on the preview until the print rebuild lands a
// few minutes later; the cost of a wrong « changed » is a second of drawing.

import { canonical, fnv1a, PDF_RENDER_VERSION } from './albumSourceHash';
import { isThemedBook } from './bookStructure';
import type { AlbumPdfInput, LayoutPage } from './pdfGenerator';

/** Bump when the fingerprint recipe changes — older manifests then redraw in full. */
export const PREVIEW_KEYS_VERSION = 1;

export interface PreviewRenderKeys {
  v: number;
  /** Everything that moves or restyles more than one page. */
  album: string;
  /** PDF pages BEFORE content page 1 (the intro), as the generator emitted them. */
  intro_pages: number;
  /** One per content page, in order (input.pages). */
  pages: string[];
  /** The cover wrap. */
  cover: string;
}

/** A signed URL names a FILE plus a token that changes on every signing — keep the file. */
function stripUrlTokens(value: unknown): unknown {
  if (typeof value === 'string') {
    return /^https?:\/\//i.test(value) ? value.split('?')[0] : value;
  }
  if (Array.isArray(value)) return value.map(stripUrlTokens);
  if (value instanceof Map) return null;                 // templatesById — handled per page
  if (value instanceof Uint8Array) return `bytes:${value.length}`;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === 'function') continue;
      out[k] = stripUrlTokens(v);
    }
    return out;
  }
  return value;
}

function digest(value: unknown): string {
  const json = JSON.stringify(canonical(stripUrlTokens(value)));
  return `${fnv1a(json)}-${json.length.toString(16)}`;
}

/** Interior-only inputs, or inputs that change per pass without changing the drawing. */
const NOT_COVER_INPUTS = new Set<string>([
  'pages', 'photos', 'templatesById', 'templatePhotoFits', 'introElements',
  'introPageUrl', 'outroPageUrl', 'pageIndices', 'importedInteriorPdf',
  'photoGapMm', 'photoGapColor', 'layoutRules',
  // Per pass, not per book: telemetry id, the admin's cut-guide ticks, the hook.
  'sessionId', 'showAssemblyGuides', 'onInteriorPlan',
]);

/** The photos a page draws — by FILE and measured shape, not by URL. */
function photosOnPage(pageJson: string, input: AlbumPdfInput): unknown[] {
  return input.photos
    .filter((p) => p.id && pageJson.includes(p.id))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((p) => ({
      id: p.id,
      file: p.storage_path ?? null,
      w: p.width ?? null,
      h: p.height ?? null,
      face: p.face_data ?? null,
    }));
}

function pageKey(lp: LayoutPage, input: AlbumPdfInput): string {
  const pageJson = JSON.stringify(lp);
  const tplId = (lp as { template_id?: string | null }).template_id ?? null;
  const tpl = tplId ? input.templatesById?.get(tplId) ?? null : null;
  return digest({
    page: lp,
    fit: input.templatePhotoFits?.[lp.page] ?? null,
    tpl: tpl ? { slots: tpl.slots, n: tpl.slot_count, overlay: tpl.overlay_print_png_url ?? null, svg: tpl.original_svg_url ?? null } : null,
    photos: photosOnPage(pageJson, input),
  });
}

/**
 * The fingerprints of what `input` draws. Pure.
 *
 * `introPages` is what the generator actually emitted before the content (it
 * reports it through `onInteriorPlan`): an intro that failed to embed shifts
 * every page number, so it is observed, never assumed.
 */
export function previewRenderKeys(input: AlbumPdfInput, introPages: number): PreviewRenderKeys {
  const pages = input.pages ?? [];
  const album = digest({
    v: PDF_RENDER_VERSION,
    k: PREVIEW_KEYS_VERSION,
    count: pages.length,
    themed: isThemedBook(pages),
    gap: input.photoGapMm ?? null,
    gapColor: input.photoGapColor ?? null,
    rules: input.layoutRules ?? null,
    bleed: input.bleedMm ?? null,
    intro: isThemedBook(pages) ? null : (input.introPageUrl ?? null),
    introElements: input.introElements ?? null,
    outro: input.outroPageUrl ?? null,
    label: input.orderLabel ?? null,
  });
  const cover: Record<string, unknown> = { v: PDF_RENDER_VERSION, k: PREVIEW_KEYS_VERSION };
  for (const [k, v] of Object.entries(input)) if (!NOT_COVER_INPUTS.has(k)) cover[k] = v;
  return {
    v: PREVIEW_KEYS_VERSION,
    album,
    intro_pages: introPages,
    pages: pages.map((lp) => pageKey(lp, input)),
    cover: digest(cover),
  };
}

export type QuickPlan =
  | { mode: 'none' }
  | { mode: 'subset'; pages: number[]; cover: boolean }
  | { mode: 'full'; why: string };

/**
 * What the quick preview must redraw, given the fingerprints on the stored
 * manifest and the album as it is now. Pure.
 *
 * `pages` are 0-based content indices. Pages added or removed shift every page
 * number after them, so they redraw the whole album — as do an album-wide
 * change, a stored set from another recipe version, or most pages changing
 * (one pass over the whole album is then no slower).
 */
export function planQuickPreview(
  stored: PreviewRenderKeys | null | undefined,
  current: Pick<PreviewRenderKeys, 'v' | 'album' | 'pages' | 'cover'>,
  storedPageCount?: number | null,
): QuickPlan {
  if (!stored || typeof stored !== 'object') return { mode: 'full', why: 'no fingerprints on the stored preview' };
  if (stored.v !== current.v) return { mode: 'full', why: 'fingerprints from another recipe version' };
  if (!Array.isArray(stored.pages) || typeof stored.intro_pages !== 'number' || stored.intro_pages < 0) {
    return { mode: 'full', why: 'unreadable stored fingerprints' };
  }
  if (stored.album !== current.album) return { mode: 'full', why: 'an album-wide change' };
  if (stored.pages.length !== current.pages.length) return { mode: 'full', why: 'pages added or removed' };
  // The stored book must actually hold every content page where the keys say it is.
  if (storedPageCount != null && storedPageCount < stored.intro_pages + current.pages.length) {
    return { mode: 'full', why: 'stored preview shorter than the album' };
  }
  const pages = current.pages.map((k, i) => (k !== stored.pages[i] ? i : -1)).filter((i) => i >= 0);
  const cover = stored.cover !== current.cover;
  if (pages.length === 0 && !cover) return { mode: 'none' };
  if (pages.length > Math.max(4, Math.ceil(current.pages.length * 0.6))) {
    return { mode: 'full', why: 'most pages changed' };
  }
  return { mode: 'subset', pages, cover };
}

/** The interior PDF page number (1-based) of content page `contentIndex` (0-based). */
export function pdfPageOfContent(contentIndex: number, introPages: number): number {
  return introPages + contentIndex + 1;
}

/** Is the switch on? Missing setting/column = ON (migration 253 may not have run). */
export function quickPreviewEnabled(settings: { quick_preview_enabled?: boolean | null } | null | undefined): boolean {
  return settings?.quick_preview_enabled !== false;
}

/**
 * The manifest date the quick pass writes. Pure.
 *
 * The admin's clock is not the server's. The date must read as NEWER than the
 * album's last save (server time) or the « version précédente » banner would
 * stay up over pages that are current — so it is never earlier than that save.
 */
export function quickManifestDate(nowMs: number, lastSavedAt: string | null | undefined): string {
  const saved = Date.parse(String(lastSavedAt ?? ''));
  return new Date(Number.isFinite(saved) ? Math.max(nowMs, saved + 1000) : nowMs).toISOString();
}

/**
 * The input for redrawing SOME content pages: just those pages, in order, and
 * nothing before them — so page j of the result is content page `pages[j]`.
 * Pure.
 *
 * `pageIndices` keeps `input.pages` whole, so album-wide decisions (a themed
 * book, the side of the spine a page sits on) are taken exactly as in the full
 * book. Bookends go (they did not change — the album key says so). The
 * marry-label is drawn on the first content page only when the real book has
 * no intro page AND that first page is part of this redraw.
 */
export function subsetInput(input: AlbumPdfInput, pages: number[], introPages: number): AlbumPdfInput {
  const firstIsBookStart = pages[0] === 0 && introPages === 0;
  return {
    ...input,
    introPageUrl: null,
    outroPageUrl: null,
    introElements: [],
    pageIndices: pages,
    orderLabel: firstIsBookStart ? input.orderLabel : undefined,
  };
}
